import { useState } from "react";
import { CheckCircle2, Loader2, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const inputCls = "h-11 rounded-sm border-white/15 bg-[#0A192F] text-white placeholder:text-slate-500 focus-visible:ring-[#00E5FF]";

const PhotoField = ({ f, value, onChange, setErr }) => {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setErr(f.name, "Use a JPG, PNG or WEBP image under 5 MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(f.uploadUrl, fd);
      onChange(r.data.path);
      setPreview(URL.createObjectURL(file));
      setErr(f.name, null);
    } catch (er) {
      setErr(f.name, errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  return (
    <label data-testid={`field-${f.name}`} className="flex cursor-pointer items-center gap-4 rounded-sm border border-dashed border-white/20 bg-[#0A192F] p-4 transition-colors hover:border-[#00E5FF]">
      {preview ? <img src={preview} alt="Selected" className="h-16 w-16 rounded-sm object-cover" /> : <Upload className="h-6 w-6 text-[#00E5FF]" />}
      <span className="text-sm text-slate-300">{busy ? "Uploading..." : value ? "Photo uploaded. Click to replace." : "Upload a clear photograph (JPG/PNG/WEBP, max 5 MB)"}</span>
      <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={pick} />
    </label>
  );
};

const FieldInput = ({ f, value, onChange, setErr }) => {
  const tid = `field-${f.name}`;
  if (f.type === "textarea") return <Textarea id={f.name} data-testid={tid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} maxLength={f.max || 2000} className="min-h-[110px] rounded-sm border-white/15 bg-[#0A192F] text-white placeholder:text-slate-500" />;
  if (f.type === "select") return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={f.name} data-testid={tid} className={inputCls}><SelectValue placeholder={f.placeholder || "Select"} /></SelectTrigger>
      <SelectContent className="border-white/15 bg-[#112240] text-white">
        {f.options.map((o) => <SelectItem key={o} value={o} data-testid={`${tid}-opt-${o.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  if (f.type === "photo") return <PhotoField f={f} value={value} onChange={onChange} setErr={setErr} />;
  return <Input id={f.name} data-testid={tid} type={f.type || "text"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} maxLength={f.max || 200} className={inputCls} />;
};

function validate(fields, v) {
  const errs = {};
  fields.forEach((f) => {
    const val = v[f.name];
    if (f.required && !String(val ?? "").trim()) errs[f.name] = "Required";
    else if (val && f.type === "email" && !/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(val)) errs[f.name] = "Enter a valid email";
    else if (val && f.type === "tel" && !/^(\+?91[\s-]?)?[6-9]\d{9}$/.test(val.replace(/\s/g, ""))) errs[f.name] = "Enter a valid 10-digit mobile number";
    else if (val && f.type === "url" && !/^https?:\/\/\S+$/.test(val)) errs[f.name] = "Must start with http:// or https://";
  });
  return errs;
}

export default function EnquiryForm({ fields, endpoint, consentText, submitLabel = "Submit", success, testid = "enquiry-form", consent = true }) {
  const init = Object.fromEntries(fields.map((f) => [f.name, ""]));
  const [values, setValues] = useState({ ...init, website_hp: "" });
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [serverErr, setServerErr] = useState("");

  const setErr = (k, m) => setErrors((e) => ({ ...e, [k]: m }));
  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(fields, values);
    if (consent && !agree) errs.consent = "Please provide consent to continue";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setState("loading");
    setServerErr("");
    try {
      await api.post(endpoint, { ...values, consent: consent ? agree : true });
      setState("done");
    } catch (er) {
      setServerErr(errMsg(er));
      setState("idle");
    }
  };

  if (state === "done") return (
    <div data-testid={`${testid}-success`} className="beam rounded-md bg-[#112240] p-8 sm:p-10">
      <CheckCircle2 className="h-10 w-10 text-[#00E5FF]" />
      <h3 className="mt-4 font-display text-3xl uppercase">Submission received</h3>
      <p className="mt-3 max-w-xl text-slate-300">{success}</p>
    </div>
  );

  return (
    <form onSubmit={submit} noValidate data-testid={testid} className="beam rounded-md bg-[#112240]/80 p-6 sm:p-8">
      <div className="grid gap-5 sm:grid-cols-2">
        {fields.map((f) => (
          <div key={f.name} className={f.full || f.type === "textarea" || f.type === "photo" ? "sm:col-span-2" : ""}>
            <Label htmlFor={f.name} className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-300">
              {f.label}{f.required ? <span className="text-[#FF5722]"> *</span> : <span className="font-normal normal-case tracking-normal text-slate-500"> (optional)</span>}
            </Label>
            <FieldInput f={f} value={values[f.name]} onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))} setErr={setErr} />
            {f.hint && <p className="mt-1.5 text-xs text-slate-500">{f.hint}</p>}
            {errors[f.name] && <p data-testid={`error-${f.name}`} className="mt-1.5 text-xs font-semibold text-[#ff7a59]">{errors[f.name]}</p>}
          </div>
        ))}
      </div>
      <input type="text" name="website_hp" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={values.website_hp} onChange={(e) => setValues((s) => ({ ...s, website_hp: e.target.value }))} />
      {consent && (
        <div className="mt-6">
          <label className="flex items-start gap-3 text-sm text-slate-300">
            <Checkbox data-testid={`${testid}-consent`} checked={agree} onCheckedChange={(c) => setAgree(!!c)} className="mt-0.5 border-white/40 data-[state=checked]:bg-[#00E5FF] data-[state=checked]:text-[#0A192F]" />
            <span>{consentText}</span>
          </label>
          {errors.consent && <p data-testid="error-consent" className="mt-1.5 text-xs font-semibold text-[#ff7a59]">{errors.consent}</p>}
        </div>
      )}
      {serverErr && <p data-testid={`${testid}-server-error`} className="mt-5 rounded-sm border border-[#FF5722]/40 bg-[#FF5722]/10 p-3 text-sm text-[#ffb199]">{serverErr}</p>}
      <button type="submit" disabled={state === "loading"} data-testid={`${testid}-submit`} className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#00E5FF] px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-[#0A192F] transition-colors hover:bg-[#5cefff] disabled:opacity-60">
        {state === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
        {submitLabel}
      </button>
    </form>
  );
}
